import { useMemo } from "react";
import Button from "@src/components/Button";
import { UNITAS_PROJECT_ID, UNITAS_XGLD_PROJECT_ID, RWALPHA_PROJECT_ID } from "@src/api/por";
import usePorDashboard from "../hooks/usePorDashboard";
import porIllustration from "./assets/por-illustration.png";
import logoUnitas from "./assets/logo-unitas.png";
import logoRwalpha from "./assets/logo-rwalpha.svg";
import HeroReservesStat from "./HeroReservesStat";
import PortfolioStatValue from "./PortfolioStatValue";
import PortfolioVerifiedAt from "./PortfolioVerifiedAt";
import PortfolioReservesSourceStack from "./PortfolioReservesSourceStack";
import { PORTFOLIO_RESERVE_SOURCES } from "./portfolioReserveSources";
import {
  formatHeroReservesSum,
  formatLastVerifiedParts,
  formatProtocolReserves,
  formatReservesBackingRatio,
  hasMeaningfulTotalSupply,
} from "./formatters";
import styles from "./index.module.scss";

const LEARN_MORE_LINK = "https://github.com/primus-labs/PoR-docs";
const REQUEST_DEMO_LINK =
  "https://docs.google.com/forms/d/e/1FAIpQLSc9ijOzKQla4oOpSytvf4K3hjrfxAT-dGM0VUIFXAR94qn5Qw/viewform";
const UNITAS_DASHBOARD = "https://por.primuslabs.xyz/unitas";
const UNITAS_XGLD_DASHBOARD = "https://por.primuslabs.xyz/unitas/xgld";
const RWALPHA_DASHBOARD = "https://por.primuslabs.xyz/rwalpha";

const UNITAS_DASHBOARD_CACHE_KEY = "por-unitas-dashboard";
const UNITAS_XGLD_DASHBOARD_CACHE_KEY = "por-unitas-xgld-dashboard";
const RWALPHA_DASHBOARD_CACHE_KEY = "por-rwalpha-dashboard";

const PORTFOLIO_BASE = [
  {
    name: "Unitas",
    logo: logoUnitas,
    href: UNITAS_DASHBOARD,
    reserveSources: PORTFOLIO_RESERVE_SOURCES.unitas,
    reserves: "",
    ratio: "",
    verifiedAt: null,
    isLoading: true,
  },
  {
    name: "Unitas XGLD",
    logo: logoUnitas,
    href: UNITAS_XGLD_DASHBOARD,
    reserveSources: PORTFOLIO_RESERVE_SOURCES.unitasXgld,
    reserves: "",
    ratio: "",
    verifiedAt: null,
    isLoading: true,
  },
  {
    name: "RWAlpha",
    logo: logoRwalpha,
    href: RWALPHA_DASHBOARD,
    reserveSources: PORTFOLIO_RESERVE_SOURCES.rwalpha,
    reserves: "",
    ratio: "",
    verifiedAt: null,
    isLoading: true,
  },
];

const FEATURES = [
  {
    num: "01",
    title: ["Secure", "attestation"],
    body: "Verify asset authenticity via zkTLS while maintaining full control over private credentials.",
  },
  {
    num: "02",
    title: ["Automated", "execution"],
    body: "Automate proof-of-reserves with customizable, real-time verification intervals.",
  },
  {
    num: "03",
    title: ["Customized", "disclosure"],
    body: "Aggregate data securely using zkVM with flexible, selective disclosure.",
  },
  {
    num: "04",
    title: ["Consolidated", "reserves"],
    body: "Combine all sources into a single, fully verifiable reserve total.",
  },
];

function buildPortfolioCard(base, dashboard, isLoading) {
  if (isLoading) {
    return { ...base, isLoading: true, showRatio: true, ratio: null };
  }

  if (!dashboard) {
    return {
      ...base,
      isLoading: false,
      reserves: "—",
      ratio: null,
      verifiedAt: null,
      showRatio: false,
    };
  }

  const ratio = formatReservesBackingRatio(dashboard.reservesBackingRatio);
  const showRatio = hasMeaningfulTotalSupply(dashboard) && ratio != null;

  return {
    ...base,
    isLoading: false,
    reserves: formatProtocolReserves(dashboard.protocolTotalReserves) ?? "—",
    ratio,
    verifiedAt: formatLastVerifiedParts(dashboard.lastVerifiedTime) ?? null,
    showRatio,
  };
}

function buildPortfolios(
  unitasDashboard,
  xgldDashboard,
  rwalphaDashboard,
  isUnitasLoading,
  isXgldLoading,
  isRwalphaLoading
) {
  const [unitasBase, xgldBase, rwalphaBase] = PORTFOLIO_BASE;

  return [
    buildPortfolioCard(unitasBase, unitasDashboard, isUnitasLoading),
    buildPortfolioCard(xgldBase, xgldDashboard, isXgldLoading),
    buildPortfolioCard(rwalphaBase, rwalphaDashboard, isRwalphaLoading),
  ];
}

function HeroSection({ heroStats, isHeroStatsSettled }) {
  return (
    <section className={styles.heroSection}>
      <div className={styles.heroInner}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Verifiable financial transparency with
            <br />
            full privacy
          </h1>
          <p className={styles.heroDesc}>
            Transparent, real-time on-chain and off-chain portfolio verification. Secured by cryptographic proofs and independent attestations.
          </p>
          <div className={styles.heroActions}>
            <Button link={LEARN_MORE_LINK} className={styles.heroBtn} isDark>
              Learn More
            </Button>
            <Button
              link={REQUEST_DEMO_LINK}
              className={`${styles.heroBtn} ${styles.heroBtnSecondary}`}
              type="secondary"
              isDark
            >
              Request a Demo
            </Button>
          </div>
          <div className={styles.heroStats}>
            <div className={`${styles.heroStatItem} ${styles.heroStatItemReserves}`}>
              <HeroReservesStat
                value={heroStats[0].value}
                isSettled={isHeroStatsSettled}
              />
              <p className="body-2">{heroStats[0].label}</p>
            </div>
            <div className={styles.heroStatItem}>
              <p className="h3">{heroStats[1].value}</p>
              <p className="body-2">{heroStats[1].label}</p>
            </div>
            <div className={`${styles.heroStatItem} ${styles.heroStatItemDesktopOnly}`}>
              <p className="h3">{heroStats[2].value}</p>
              <p className="body-2">{heroStats[2].label}</p>
            </div>
          </div>
        </div>
        <div className={styles.heroIllustration}>
          <img src={porIllustration} alt="Proof-of-Reserves assets portfolio illustration" />
        </div>
      </div>
    </section>
  );
}

function PortfolioCard({
  name,
  logo,
  href,
  reserveSources,
  reserves,
  ratio,
  verifiedAt,
  isLoading = false,
  showRatio = true,
}) {
  return (
    <div className={styles.portfolioCard}>
      <div className={styles.portfolioCardBody}>
        <div className={styles.portfolioCardHeader}>
          <div className={styles.portfolioCardBrand}>
            <img className={styles.portfolioLogo} src={logo} alt={name} />
            <p className="h4">{name}</p>
          </div>
          <div className={styles.portfolioLive}>
            <span className={styles.portfolioLiveDot} aria-hidden />
            <span className={styles.portfolioLiveLabel}>Live</span>
          </div>
        </div>
        <div className={styles.portfolioDivider} aria-hidden />
        <div className={styles.portfolioStat}>
          <p className="body-2">Verified Reserves</p>
          <div className={styles.portfolioReservesRow}>
            <PortfolioStatValue value={reserves} isLoading={isLoading} />
            <PortfolioReservesSourceStack sources={reserveSources} />
          </div>
        </div>
        {(showRatio || isLoading) && (
          <div className={`${styles.portfolioStat} ${styles.portfolioStatRatio}`}>
            <p className="body-2">Reserves Backing Ratio</p>
            <PortfolioStatValue value={ratio} isLoading={isLoading && showRatio} />
          </div>
        )}
        <div className={styles.portfolioStat}>
          <p className="body-2">Latest Verified</p>
          <PortfolioVerifiedAt verifiedAt={verifiedAt} isLoading={isLoading} />
        </div>
      </div>
      <Button link={href} isDark className={styles.portfolioBtn}>
        View Details
      </Button>
    </div>
  );
}

function PortfoliosSection({ portfolios }) {
  return (
    <section className={styles.portfoliosSection}>
      <div className={styles.portfoliosInner}>
        <h2 className={`h2 ${styles.portfoliosTitle}`}>Verified portfolios</h2>
        <div
          className={`${styles.portfolioGrid} ${
            portfolios.length === 2 ? styles.portfolioGridTwo : styles.portfolioGridThree
          }`}
        >
          {portfolios.map((portfolio) => (
            <PortfolioCard key={portfolio.name} {...portfolio} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ num, title, body }) {
  return (
    <div className={styles.featureCard}>
      <p className={styles.featureNumber}>{num}</p>
      <div className={styles.featureCardContent}>
        <p className={styles.featureCardTitle}>
          <span className={styles.featureCardTitleLead}>{title[0]}</span>
          <br className={styles.featureCardTitleBr} />
          <span>{title[1]}</span>
        </p>
        <p className="body-1-regular">{body}</p>
      </div>
    </div>
  );
}

function FeaturesSection() {
  return (
    <section className={styles.featuresSection}>
      <div className={styles.featuresInner}>
        <div className={styles.featuresHeader}>
          <h2 className={`h2 ${styles.featuresHeading}`}>
            Independent
            <br />
            proof-of-reserves
          </h2>
          <Button link={REQUEST_DEMO_LINK} className={styles.heroBtn} type="secondary" isDark>
            Get in touch
          </Button>
        </div>
        <div className={styles.featuresGrid}>
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.num} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

function sumProtocolReserves(...dashboards) {
  const values = dashboards
    .map((dashboard) => Number(dashboard?.protocolTotalReserves))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return null;
  }

  return values.reduce((total, value) => total + value, 0);
}

export default function PorLandingPage() {
  const { dashboard: unitasDashboard, isSettled: isUnitasSettled } = usePorDashboard(
    UNITAS_PROJECT_ID,
    UNITAS_DASHBOARD_CACHE_KEY
  );
  const { dashboard: xgldDashboard, isSettled: isXgldSettled } = usePorDashboard(
    UNITAS_XGLD_PROJECT_ID,
    UNITAS_XGLD_DASHBOARD_CACHE_KEY
  );
  const { dashboard: rwalphaDashboard, isSettled: isRwalphaSettled } = usePorDashboard(
    RWALPHA_PROJECT_ID,
    RWALPHA_DASHBOARD_CACHE_KEY
  );

  const isHeroStatsSettled = isUnitasSettled && isXgldSettled && isRwalphaSettled;
  const isUnitasLoading = !isUnitasSettled && !unitasDashboard;
  const isXgldLoading = !isXgldSettled && !xgldDashboard;
  const isRwalphaLoading = !isRwalphaSettled && !rwalphaDashboard;

  const portfolios = useMemo(
    () =>
      buildPortfolios(
        unitasDashboard,
        xgldDashboard,
        rwalphaDashboard,
        isUnitasLoading,
        isXgldLoading,
        isRwalphaLoading
      ),
    [
      unitasDashboard,
      xgldDashboard,
      rwalphaDashboard,
      isUnitasLoading,
      isXgldLoading,
      isRwalphaLoading,
    ]
  );

  const heroStats = useMemo(() => {
    const totalReserves = sumProtocolReserves(
      unitasDashboard,
      xgldDashboard,
      rwalphaDashboard
    );

    return [
      {
        value: formatHeroReservesSum(totalReserves) ?? "—",
        label: "Reserves Verified",
      },
      {
        value: "< 5 Days",
        label: "Integration Time",
      },
      {
        value: "Included",
        label: "Monitoring & Updates",
      },
    ];
  }, [unitasDashboard, xgldDashboard, rwalphaDashboard]);

  return (
    <div className={styles.page}>
      <HeroSection heroStats={heroStats} isHeroStatsSettled={isHeroStatsSettled} />
      <PortfoliosSection portfolios={portfolios} />
      <FeaturesSection />
    </div>
  );
}
